import { useLocation } from 'react-router';
import { DASHBOARDS_PATH } from '../../routes/paths';
import type { Control, ControlSelections } from '../types/controls';
import { useAppliedDashboardFilters } from './applied/useAppliedDashboardFilters';
import { useEditFilter } from './edit/useEditFilter';
import { useFilterMemory } from './memory/useFilterMemory';
import { useTemplateControls } from './templateControls';

const isDashboardRouteFor = (pathname: string, templateId: string | undefined): boolean => {
  if (!templateId) return false;
  const base = `${DASHBOARDS_PATH}/${templateId}`;
  return pathname === base || pathname.startsWith(`${base}/`);
};

export type DashboardFiltersController = {
  stagedSelections: ControlSelections;
  stagedTimeRangeOverride: string | undefined;
  hasUnappliedChanges: boolean;
  setControlValueAndResetDependents: (key: string, values: string[]) => void;
  stageTimeRange: (value: string) => void;
  applyStagedFilters: () => void;
  applyControlValueNow: (key: string, values: string[]) => void;
};

export type UseDashboardFiltersOptions = {
  templateId: string | undefined;
  controls?: Control[];
  templateDefaultTimeRange?: string;
  accessibleInstanceIds?: string[];
};

/** The URL holds the criteria, applied or not, and `prefill` says which — so the bar is the URL
 * and the Apply button is that marker. What renders is frozen separately, see `useRenderedFilters`. */
export const useDashboardFilters = ({
  templateId,
  controls = [],
  templateDefaultTimeRange,
  accessibleInstanceIds = [],
}: UseDashboardFiltersOptions): DashboardFiltersController => {
  const { pathname } = useLocation();
  // The panel outlives the route by a commit when leaving, long enough to write the next
  // dashboard's URL under this template's name.
  const ownsTheRoute = isDashboardRouteFor(pathname, templateId);

  const applied = useAppliedDashboardFilters(controls);
  const template = useTemplateControls(controls);

  const { rememberFilters } = useFilterMemory({
    templateId,
    template,
    applied,
    templateDefaultTimeRange,
    accessibleInstanceIds,
    ownsTheRoute,
  });

  const editing = useEditFilter({ controls, template, applied, onApplied: rememberFilters });

  return {
    stagedSelections: applied.selectionsByControlKey,
    stagedTimeRangeOverride: applied.timeRangeOverride,
    hasUnappliedChanges: !applied.isApplied,
    ...editing,
  };
};
